<script lang="ts" setup>
import AlertsTable from '@/components/alerts/AlertsTable.vue';
import AlertsFilters from '@/components/alerts/AlertsFilters.vue';
import Card from 'primevue/card';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { getFundingRateGroupsTableColumns } from '@/columns/fundingRateGroupsColumns';
import { useFundingRateGroupsStore } from '@/stores/fundingRateGroupsStore';
import isUndefined from 'lodash/isUndefined';
import isNull from 'lodash/isNull';
import { convertPeriod, keysToSnake } from '@/utils';
import { createFundingRatesFilters } from '@/filters/fundingRateGroupsFilters';
import { z } from "zod"
import { UPDATE_DATA_DELAY, FUNDING_GROUP_EXCHANGES, AVAILABLE_EXCHANGES_LIST } from '@/utils/consts';
import { stringListFormatter, loadFromStorage, parseCommaSeparatedAssets } from '@/utils';
import { extractFormData, extractZodFromForm } from "@/utils/forms"
import type { FundingRateGroupResponse } from '@/services/funding-rates-v2-service/types';
import type { FormSubmitEvent } from '@primevue/forms';
import { Divider } from 'primevue';

let updateRulesTimer = 0
let updateDataTimer = 0

const filters = createFundingRatesFilters()
const hiddenExs = ref<string[]>([])
const pageInited = ref(false)
const sourceTableColumns = getFundingRateGroupsTableColumns()
const fundingRateGroupsStore = useFundingRateGroupsStore()
const formattingRules = ref({})
const alertsTable = ref<typeof AlertsTable>()
const hideColumns = computed(() => hiddenExs.value.flatMap(p => [`${p}_rate`, `${p}_period`, `${p}_link`]))
const tableColumns = computed(() => {
  if (hideColumns.value.length == 0) {
    return sourceTableColumns
  } else {
    return sourceTableColumns.filter(x => !hideColumns.value.includes(x.field))
  }
})

// данные формы + zod-правила
const formData = ref(extractFormData(filters))
const zodObjectData = extractZodFromForm(filters)
const zodForm = z.object(zodObjectData)

const updateRules = () => {
  formattingRules.value = {
    'symbol.name': (v: string) => {
      if (isUndefined(v) || isNull(v)) {
        return ''
      }
      return v.split('/')[0]
    },
    ...genExRules()
  }
}
const genExRules = () => {
  const newRules: Record<string, (v: string, data: FundingRateGroupResponse) => string | number> = {}
  for (const ex of FUNDING_GROUP_EXCHANGES) {
    newRules[`${ex}_rate`] = (v: string, data: FundingRateGroupResponse) => {
      if (!data.exchanges?.[ex]?.current_rate) {
        return '—'
      }
      return Number(data.exchanges?.[ex]?.current_rate).toFixed(4)
    }
    newRules[`${ex}_period`] = (v: string, data: FundingRateGroupResponse) => {
      if (!data.exchanges?.[ex]?.next_ts) {
        return '—'
      }
      const lot = convertPeriod(data.exchanges?.[ex]?.next_ts)
      if (lot?.startsWith('-')) {
        return '—'
      }
      return lot ?? '—'
    }
  }
  return newRules
}

onMounted(async () => {
  fundingRateGroupsStore.clear()
  updateRules()
  updateRulesTimer = window.setInterval(updateRules, 1000)

  const exs = loadFromStorage<string[]>('fundingExsFilter', stringListFormatter, FUNDING_GROUP_EXCHANGES).filter((x: string) => AVAILABLE_EXCHANGES_LIST.includes(x))
  formData.value.exchanges = exs

  fundingRateGroupsStore.filters.exchanges = formData.value.exchanges
  hiddenExs.value = getColsToHide()
  await alertsTable.value?.onPage()

  pageInited.value = true

  // Обновляем контент каждые X секунд
  updateDataTimer = window.setTimeout(refresher, UPDATE_DATA_DELAY)
})

const refresher = () => {
  alertsTable.value?.refresh()
  updateDataTimer = window.setTimeout(refresher, UPDATE_DATA_DELAY)
}

onUnmounted(() => {
  if (updateRulesTimer) clearInterval(updateRulesTimer)
  if (updateDataTimer) clearTimeout(updateDataTimer)
})

const getColsToHide = () => {
  if (!fundingRateGroupsStore.filters.exchanges) {
    return []
  }
  const hide = []
  for (const ex of FUNDING_GROUP_EXCHANGES) {
    if (!fundingRateGroupsStore.filters.exchanges.includes(ex)) {
      hide.push(ex)
    }
  }
  return hide
}

const onFilterFormSubmit = async (e: FormSubmitEvent) => {
  if (!e.values) {
    return
  }
  const values = keysToSnake(e.values)
  if (typeof values.symbols === 'string') {
    values.symbols = parseCommaSeparatedAssets(values.symbols)
  }
  fundingRateGroupsStore.filters = values
  if (fundingRateGroupsStore.filters.exchanges) {
    localStorage.setItem('fundingExsFilter', fundingRateGroupsStore.filters.exchanges.join(';'))
    hiddenExs.value = getColsToHide()
  } else {
    fundingRateGroupsStore.filters.exchanges = FUNDING_GROUP_EXCHANGES
  }
  await alertsTable.value?.onPage(null)
}
</script>

<template>
  <Card class="md:mb-5 rounded-none md:rounded-xl" pt:body:class="p-0">
    <template #content>
      <AlertsFilters :inited="pageInited" panel-class="px-2 border-none mb-0" :loading="fundingRateGroupsStore.loading"
        :form-submit="onFilterFormSubmit" :filters="filters" :zod="zodForm"
        grid-classes="gap-y-3 gap-x-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-4" :form-data="formData">
      </AlertsFilters>
      <Divider class="mt-0 mb-0"></Divider>
      <div class="overflow-x-scroll">
        <AlertsTable :paginator="true" :lazy="true" :formatting-rules="formattingRules" ref="alertsTable"
          :columns="tableColumns" :fake-rows-count="30" :store="fundingRateGroupsStore" :col-full="false">
        </AlertsTable>
      </div>
    </template>
  </Card>
</template>
